<template>
  <main class="onepage">
    <section id="top" class="hero">
      <div class="hero-bg" aria-hidden="true" />
      <div class="hero-inner">
          <div class="hero-copy">
            <p class="kicker">FishROS</p>
            <h1>机器人开发生态与产品</h1>
            <p class="hero-lead">
              提供 ROS/ROS2 开发常用工具、硬件产品与配置能力：一键安装 ROS、书籍资料、产品与视频演示、联系我们。
            </p>
            <div class="hero-quick">
              <router-link class="btn primary emphasis" to="/navigation">网址导航</router-link>
              <router-link class="btn consult emphasis" to="/config-assistant">配置助手</router-link>
            </div>
          </div>
        <div class="hero-card">
          <div class="hero-card-title">一键安装 ROS</div>
          <div class="cmd">
            <code>{{ installCommand }}</code>
          </div>
          <div class="hero-card-actions">
            <button class="btn primary big-copy" @click="copyInstall">复制一键安装命令</button>
            <a class="btn big-ghost" href="https://github.com/fishros/install" target="_blank" rel="noopener">给个Star</a>
          </div>
          <div class="hero-card-sub">支持 Chrome/Edge；复制后粘贴到终端执行。</div>
        </div>
      </div>
    </section>


    <section id="book" class="section">
      <header class="section-head">
        <h2>书籍</h2>
      </header>
      <div class="book">
        <div class="book-card">
          <div class="book-layout">
            <div class="book-cover">
              <img class="book-img" :src="bookCover" alt="ROS2机器人开发从入门到实践 封面" loading="lazy">
            </div>
            <div class="book-body">
              <div class="book-title">ROS2机器人开发：从入门到实践</div>
              <div class="book-desc">面向入门与工程落地，覆盖开发流程与实践案例。</div>
              <div class="book-actions">
                <a class="btn" :href="bookRepoUrl" target="_blank" rel="noopener">获取源码</a>
                <a class="btn primary" :href="bookBuyUrl" target="_blank" rel="noopener">立即购买</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="products" class="section">
      <header class="section-head">
        <h2>产品</h2>
        <p>FishBot、M2 大负载机器人、四足巡检机械狗等产品与解决方案。</p>
      </header>
      <div v-if="productsLoading" class="muted" style="margin-top: 14px; text-align: center;">加载中...</div>
      <div v-else>
        <div v-for="group in productGroups" :key="group.key" class="product-group">
          <div class="product-group-title">{{ group.title }}</div>
          <div class="grid">
            <article class="product" v-for="p in group.items" :key="p.id">
              <div class="product-video">
                <iframe
                  v-if="p.playerUrl"
                  :src="playerUrlNoAutoplay(p.playerUrl)"
                  scrolling="no"
                  frameborder="0"
                  allowfullscreen
                  loading="lazy"
                />
                <div v-else class="video-placeholder">
                  <div class="ph-title">{{ p.title }}</div>
                  <div class="muted" style="font-size: 12px;">未配置视频</div>
                </div>
                <div class="product-badge">{{ productCategoryLabel(p.category) }}</div>
              </div>
              <h3>{{ p.title }}</h3>
              <p class="muted">{{ p.desc }}</p>
              <div class="product-actions">
                <a v-if="p.buyUrl" class="btn small primary emphasis" :href="p.buyUrl" target="_blank" rel="noopener">淘宝</a>
                <button class="btn small consult emphasis" type="button" @click="scrollTo('contact')">咨询</button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section id="videos" class="section">
      <header class="section-head">
        <h2>教程&视频</h2>
        <p>教程与视频内容汇总</p>
      </header>
      <div v-if="tutorialVideosLoading" class="muted" style="margin-top: 14px; text-align: center;">加载中...</div>
      <div v-else class="video-grid">
        <div class="video" v-for="v in tutorialVideos" :key="v.id">
          <div class="video-title">{{ v.title }}</div>
          <div class="video-frame">
            <iframe
              v-if="v.playerUrl"
              :src="playerUrlNoAutoplay(v.playerUrl)"
              scrolling="no"
              frameborder="0"
              allowfullscreen
              loading="lazy"
            />
            <div v-else class="video-placeholder">
              <div class="ph-title">{{ v.title }}</div>
              <div class="muted" style="font-size: 12px;">未配置视频</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="contact" class="section contact">
      <header class="section-head">
        <h2>联系我们</h2>
        <p>关注公众号与B站，获取 ROS/ROS2 机器人开发更新。</p>
      </header>
      <div class="contact-grid">
        <div class="contact-card">
          <div class="contact-title">微信公众号</div>
          <img class="qr" :src="wechatQr" alt="FishROS 微信公众号" loading="lazy">
          <div class="muted">鱼香ROS官方公众号</div>
        </div>

        <div class="contact-card">
          <div class="contact-title">商务合作&产品咨询</div>
          <img class="qr" :src="businessWxQr" alt="商务合作&产品咨询 微信" loading="lazy">
          <div class="muted">添加微信，备注：合作/咨询</div>
        </div>

        <div class="contact-card">
          <div class="contact-title">社交媒体</div>
          <a class="btn small" href="https://space.bilibili.com/1940177928" target="_blank" rel="noopener">B站主页</a>
          <a class="btn small" href="http://github.com/fishros/" target="_blank" rel="noopener">GitHub</a>
          <a class="btn small" href="https://www.zhihu.com/people/fishros" target="_blank" rel="noopener">知乎</a>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { getProducts, getTutorialVideos } from '@/server.js'

export default {
  data () {
    return {
      // 用 https：这行是首页给用户直接复制去执行的命令，而脚本是以 root 跑的。
      // 走 http 的话第一跳是明文，中间人可以在 301 跳转生效前就把整个脚本换掉，
      // 后面脚本内部再怎么用 https 校验证书都没有意义。
      installCommand: 'source <(wget -qO- https://fishros.com/install)',
      bookRepoUrl: 'https://gitee.com/fishros/ros2bookcode',
      bookBuyUrl: 'https://detail.tmall.com/item.htm?id=828967847669',
      bookCover: require('@/assets/book.jpg'),
      wechatQr: require('@/assets/wechat-mp-qr.jpg'),
      businessWxQr: require('@/assets/wechat-business-qr.jpg'),
      products: [],
      productsLoading: false,
      tutorialVideos: [],
      tutorialVideosLoading: false
    }
  },
  computed: {
    productGroups () {
      const list = Array.isArray(this.products) ? this.products : []
      const product = []
      const solution = []
      for (const item of list) {
        if (!item || item.hide) continue
        if (item.category === 'solution') solution.push(item)
        else product.push(item)
      }
      const sortFn = (a, b) => {
        const sa = typeof a.sort === 'number' ? a.sort : 9999
        const sb = typeof b.sort === 'number' ? b.sort : 9999
        return sa - sb || (a.id || 0) - (b.id || 0)
      }
      product.sort(sortFn)
      solution.sort(sortFn)
      const out = []
      if (product.length) out.push({ key: 'product', title: '产品', items: product })
      if (solution.length) out.push({ key: 'solution', title: '解决方案', items: solution })
      return out
    }
  },
  watch: {
    '$route.hash': {
      immediate: true,
      handler (h) {
        this.$nextTick(() => {
          const id = (h || '').replace('#', '')
          if (!id) return
          this.scrollTo(id)
        })
      }
    }
  },
  mounted () {
    this.loadProducts()
    this.loadTutorialVideos()
  },
  beforeDestroy () {
  },
  methods: {
    async loadProducts () {
      this.productsLoading = true
      try {
        const res = await getProducts()
        this.products = (res && res.data) ? res.data : []
      } catch (e) {
        this.products = []
        const msg = (e && e.message) ? e.message : String(e)
        this.$message.error(`产品加载失败：${msg}`)
      } finally {
        this.productsLoading = false
      }
    },
    async loadTutorialVideos () {
      this.tutorialVideosLoading = true
      try {
        const res = await getTutorialVideos()
        this.tutorialVideos = (res && res.data) ? res.data : []
      } catch (e) {
        this.tutorialVideos = []
        const msg = (e && e.message) ? e.message : String(e)
        this.$message.error(`教程&视频加载失败：${msg}`)
      } finally {
        this.tutorialVideosLoading = false
      }
    },
    productCategoryLabel (category) {
      return category === 'solution' ? '解决方案' : '产品'
    },
    bvidFromPlayerUrl (playerUrl) {
      const s = String(playerUrl || '')
      const m = s.match(/[?&]bvid=([^&]+)/i)
      return m && m[1] ? decodeURIComponent(m[1]) : ''
    },
    playerUrlNoAutoplay (playerUrl) {
      const s = String(playerUrl || '').trim()
      if (!s) return ''
      if (/([?&])autoplay=\d+/i.test(s)) {
        return s.replace(/([?&])autoplay=\d+/i, '$1autoplay=0')
      }
      return s.includes('?') ? `${s}&autoplay=0` : `${s}?autoplay=0`
    },
    async copyInstall () {
      const text = this.installCommand
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const el = document.createElement('textarea')
          el.value = text
          el.setAttribute('readonly', 'readonly')
          el.style.position = 'fixed'
          el.style.top = '-9999px'
          document.body.appendChild(el)
          el.select()
          document.execCommand('copy')
          document.body.removeChild(el)
        }
        this.$message.success('命令已复制')
        this.scrollTo('install')
      } catch (e) {
        this.$message.error('复制失败，请手动复制')
      }
    },
    scrollTo (id) {
      const el = document.getElementById(id)
      if (!el) return
      const top = el.getBoundingClientRect().top + (window.pageYOffset || 0) - 76
      window.scrollTo({ top, behavior: 'smooth' })
    },
  }
}
</script>

<style scoped>
.onepage {
  --ink: #0f172a;
  --muted: rgba(15, 23, 42, 0.7);
  --card: #ffffff;
  --line: rgba(15, 23, 42, 0.12);
  --ros: #e11d48;
  --ros2: #111827;
  width: 88%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 0 50px;
  color: var(--ink);
}

.hero {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #ececec;
}

.hero-bg {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(800px 320px at 10% 0%, rgba(225, 29, 72, 0.12), transparent 60%),
    radial-gradient(800px 320px at 90% 20%, rgba(17, 24, 39, 0.10), transparent 55%),
    linear-gradient(180deg, #ffffff, rgba(17, 24, 39, 0.02));
  pointer-events: none;
}

.hero-inner {
  position: relative;
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 22px;
  padding: 34px;
}

.kicker {
  margin: 0 0 10px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(225, 29, 72, 0.9);
}

.hero-copy h1 {
  margin: 0 0 12px;
  font-size: 38px;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: var(--ros2);
}

.hero-lead {
  margin: 0;
  color: var(--muted);
  line-height: 1.8;
  max-width: 58ch;
}

.hero-quick {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}


.hero-note {
  margin-top: 14px;
  color: rgba(15, 23, 42, 0.55);
  font-size: 12px;
}

.hero-card {
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(255, 255, 255, 0.85);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  backdrop-filter: blur(8px);
}

.hero-card-title {
  font-weight: 800;
  font-size: 20px;
  color: var(--ros2);
}

.hero-card-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.hero-card-sub {
  font-size: 13px;
  color: rgba(15, 23, 42, 0.6);
}

.btn {
  appearance: none;
  border: 1px solid rgba(15, 23, 42, 0.16);
  background: #fff;
  color: var(--ros2);
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background-color 0.12s ease, border-color 0.12s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.10);
}

.btn.primary {
  background: var(--ros);
  border-color: rgba(225, 29, 72, 0.55);
  color: #fff;
}

.btn.big-copy {
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  letter-spacing: 0.01em;
  box-shadow: 0 14px 36px rgba(225, 29, 72, 0.22);
}

.btn.big-copy:hover {
  box-shadow: 0 18px 44px rgba(225, 29, 72, 0.26);
}

.btn.big-ghost {
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  background: rgba(17, 24, 39, 0.04);
}

.btn.ghost {
  background: transparent;
  border-color: rgba(225, 29, 72, 0.45);
  color: var(--ros);
}

.btn.small {
  padding: 10px 14px;
  border-radius: 9px;
  font-size: 13px;
}

.btn.link {
  background: rgba(17, 24, 39, 0.04);
}

.btn.consult {
  background: rgba(17, 24, 39, 0.92);
  border-color: rgba(17, 24, 39, 0.92);
  color: #fff;
}

.btn.consult:hover {
  background: rgba(17, 24, 39, 1);
  border-color: rgba(17, 24, 39, 1);
}

.btn.emphasis {
  min-width: 92px;
}

.section {
  margin-top: 26px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #ececec;
  padding: 24px;
}

.section-head h2 {
  margin: 0 0 8px;
  color: #1f2b5b;
}

.section-head p {
  margin: 0;
  color: #666;
  line-height: 1.7;
}

.cmd {
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(17, 24, 39, 0.02);
  border-radius: 12px;
  padding: 14px;
  overflow: auto;
}

.cmd.wide {
  padding: 14px;
}

.cmd code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 15px;
  color: rgba(17, 24, 39, 0.95);
  white-space: pre;
}


.book {
  margin-top: 14px;
}

.book-card {
  border: none;
  border-radius: 0;
  padding: 0;
  background: transparent;
}

.book-layout {
  display: grid;
  grid-template-columns: 1fr minmax(320px, 560px) 1fr;
  gap: 14px;
  align-items: center;
}

.book-cover {
  grid-column: 1;
  display: flex;
  justify-content: flex-end;
}

.book-img {
  width: 220px;
  height: auto;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.85);
}

.book-body {
  grid-column: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.book-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.95);
  font-size: 28px;
}

.book-desc {
  margin-top: 10px;
  color: rgba(15, 23, 42, 0.7);
  line-height: 1.7;
}

.book-actions {
  margin-top: 14px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
}

.grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.product-group {
  margin-top: 16px;
}

.product-group:first-of-type {
  margin-top: 12px;
}

.product-group-title {
  text-align: center;
  font-weight: 900;
  color: rgba(17, 24, 39, 0.90);
  margin-bottom: 10px;
}

.product {
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.product-video {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background:
    radial-gradient(520px 180px at 20% 0%, rgba(225, 29, 72, 0.10), transparent 55%),
    radial-gradient(520px 180px at 80% 20%, rgba(17, 24, 39, 0.10), transparent 55%),
    linear-gradient(180deg, rgba(17, 24, 39, 0.03), rgba(17, 24, 39, 0.00));
}

.product-video iframe,
.product-video .video-placeholder {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.product-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(225, 29, 72, 0.12);
  color: rgba(225, 29, 72, 1);
  border: 1px solid rgba(225, 29, 72, 0.20);
  font-weight: 700;
}

.product h3 {
  margin: 14px 14px 8px;
  color: rgba(17, 24, 39, 0.95);
}

.muted {
  color: rgba(15, 23, 42, 0.68);
}

.product p {
  margin: 0 14px;
  line-height: 1.7;
}

.product-actions {
  margin: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.video-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.video {
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.video-title {
  padding: 12px 14px;
  font-weight: 800;
  color: rgba(17, 24, 39, 0.95);
}

.video-frame {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: rgba(17, 24, 39, 0.04);
}

.video-frame iframe,
.video-placeholder {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.video-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.ph-title {
  font-weight: 800;
  color: rgba(17, 24, 39, 0.75);
}

.linkout {
  display: inline-block;
  padding: 10px 14px 14px;
  font-size: 12px;
}

.contact-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.contact-card {
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 12px;
  padding: 16px;
  background:
    radial-gradient(520px 180px at 20% 0%, rgba(225, 29, 72, 0.08), transparent 55%),
    radial-gradient(520px 180px at 80% 20%, rgba(17, 24, 39, 0.08), transparent 55%),
    #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  text-align: center;
}

.contact-card .btn {
  align-self: center;
}

.contact-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.95);
}

.qr {
  width: 176px;
  height: 176px;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(17, 24, 39, 0.02);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.10);
}

@media screen and (max-width: 768px) {
  .onepage {
    width: 90%;
  }

  .hero-inner {
    grid-template-columns: 1fr;
    padding: 22px;
  }

  .hero-copy h1 {
    font-size: 30px;
  }

  .section {
    padding: 18px;
  }

  .grid {
    grid-template-columns: 1fr;
  }

  .video-grid {
    grid-template-columns: 1fr;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }

  .book-layout {
    grid-template-columns: 1fr;
  }

  .book-cover {
    grid-column: auto;
    justify-content: center;
  }

  .book-body {
    grid-column: auto;
  }

  .book-img {
    width: min(260px, 90%);
  }
}
</style>
